import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { RawService, RelocateRawParameters } from 'src/app/services/raw.service';
import { UploadService } from 'src/app/services/upload.service';
import { SplitComponent } from 'angular-split';
import { MatDialog } from '@angular/material/dialog';
import { MoveRawDialogComponent, MoveRawDialogData } from '../../move-raw-dialog/move-raw-dialog.component';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { lastValueFrom } from 'rxjs';
import { MatMenuTrigger } from '@angular/material/menu';
import { CacheFileService } from 'src/app/services/cache-file.service';
import { SelectionModel } from '@angular/cdk/collections';
import { TerminalComponent } from '../../terminal/terminal.component';
import { SubscriptionContainer } from 'src/app/models/subscription-container';
import { SettingsService } from 'src/app/services/settings.service';
import { DatasetsService } from 'src/app/services/datasets.service';
import { WidgetService } from 'src/app/services/widget.service';
import { DialogService } from '../../../services/dialog.service';

@Component({
    selector: 'app-raw',
    templateUrl: './raw.component.html',
    styleUrls: ['./raw.component.scss'],
    standalone: false
})
export class RawComponent implements OnInit, AfterViewInit, OnDestroy {
  private dialogs = inject(DialogService);
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(SplitComponent) split: SplitComponent;
  @ViewChild('rawfileTable') rawfileTable!: ElementRef;
  @ViewChild(TerminalComponent) terminal: TerminalComponent;

  @Input() isWidget: boolean = false;
  @Input() allDatasets: string[] = [];
  @Output() onViewData = new EventEmitter<{ path: string; fileType: string }>();

  public isLoading = true;
  public pageSize = 10;
  private allColumns: string[] = ['select', 'icon', 'fileName', 'fileTime', 'folder', 'owner', 'fileSize', 'datasets', 'view', 'cacheFile'];

  // Show folder column only in search mode, hide it in folder navigation mode
  public get displayedColumns(): string[] {
    if (this.isSearchMode) {
      return this.allColumns;
    }
    // Hide 'folder' column when navigating folders (it's redundant)
    return this.allColumns.filter(c => c !== 'folder');
  }
  public rawData = [];
  public dataSource = new MatTableDataSource<any>([]);
  public allFiles = [];
  public uploadProgress: any[] = [];
  public view: string = 'TABLE';
  public screenInfo: any = null;
  public logInfo: any = null;
  public selection = new SelectionModel<any>(true, []);
  @Input() teamName: string;
  private _subs = new SubscriptionContainer();
  public isPolling: boolean = false;
  public lastUpdated: Date;
  public terminalId = new Date().getTime().toString();
  private _pollingTimer?: number;
  public datasetOperationLoading: Map<string, boolean> = new Map(); // Track loading per row
  private lastClickedRow: any = null; // Track last clicked row for shift-click range selection
  public showDropZone = false;
  private dragCounter = 0; // Track nested drag enter/leave events

  // Folder navigation state
  public currentFolder: string = ''; // Empty string = root of raw data folder
  public isSearchMode: boolean = false; // True when user is searching recursively
  private _filterValue: string = '';

  public get hasSelected(): boolean {
    return this.selection.selected.length !== 0;
  }

  // Check if we can go up a directory
  public get canGoUp(): boolean {
    return this.currentFolder !== '';
  }

  // Get current folder display name
  public get currentFolderDisplay(): string {
    if (!this.currentFolder) return 'raw';
    return this.currentFolder.split('/').pop() || 'raw';
  }

  // Get breadcrumb segments
  public get breadcrumbs(): string[] {
    if (!this.currentFolder) return ['raw'];
    return ['raw', ...this.currentFolder.split('/').filter(s => s)];
  }

  private _namecat = 30;
  public get namecat(): number {
    return this._namecat;
  }

  private $loadNewRawData(data: any[]) {
    this.rawData = data;
    this.rawData.sort((a, b) => Date.parse(b.fileTime) - Date.parse(a.fileTime));
    this.allFiles = this.rawData;
    this.isLoading = false;

    // Apply folder view
    this.updateDataSourceForCurrentFolder();

    if (this.selection) {
      this.selection.clear();
    }
    this.applyTableSettings();
  }

  // Update the data source to show only items in the current folder
  private updateDataSourceForCurrentFolder(): void {
    if (this.isSearchMode) {
      // In search mode, show all matching files recursively
      this.dataSource.data = this.rawData;
      return;
    }

    // Get items in current folder (files and immediate subfolders)
    const itemsInFolder = this.getItemsInCurrentFolder();
    this.dataSource.data = itemsInFolder;
  }

  // Get files and folders in the current directory
  private getItemsInCurrentFolder(): any[] {
    const result: any[] = [];
    const seenFolders = new Set<string>();

    // The base path for comparison (e.g., "/data/teamname/raw" + currentFolder)
    // Files have a 'folder' property that is their parent directory

    for (const file of this.rawData) {
      // Handle empty folder items from the API (have isEmptyFolder: true)
      if (file.isEmptyFolder) {
        // For empty folders, fullPath IS the folder path, and folder is the parent
        const fullPathParts = file.fullPath.split('/');
        const rawIndex = fullPathParts.indexOf('raw');
        const relativeEmptyFolderPath = rawIndex >= 0 ? fullPathParts.slice(rawIndex + 1).join('/') : '';
        const parentRelativePath = relativeEmptyFolderPath.split('/').slice(0, -1).join('/');
        const folderName = fullPathParts[fullPathParts.length - 1];

        // Check if this empty folder should be displayed in the current view
        if (this.currentFolder === parentRelativePath && !seenFolders.has(folderName)) {
          seenFolders.add(folderName);
          result.push({
            isFolder: true,
            folderName: folderName,
            folderPath: relativeEmptyFolderPath,
            fileTime: file.fileTime,
            folder: file.folder
          });
        }
        continue;
      }

      // file.folder is something like "/data/local/raw" or "/data/local/raw/subfolder"
      // We need to extract the relative path from the raw root

      // Get the relative folder path (strip the /data/{team}/raw prefix)
      const folderParts = file.folder.split('/');
      const rawIndex = folderParts.indexOf('raw');
      const relativePath = rawIndex >= 0 ? folderParts.slice(rawIndex + 1).join('/') : '';

      if (this.currentFolder === '') {
        // At root: show files in raw root and immediate subfolders
        if (relativePath === '') {
          // File is directly in raw folder
          result.push(file);
        } else {
          // File is in a subfolder - show the immediate subfolder
          const immediateFolder = relativePath.split('/')[0];
          if (!seenFolders.has(immediateFolder)) {
            seenFolders.add(immediateFolder);
            result.push({
              isFolder: true,
              folderName: immediateFolder,
              folderPath: immediateFolder,
              fileTime: file.fileTime, // Use most recent file time
              folder: file.folder.split('/').slice(0, rawIndex + 2).join('/') // Path to this subfolder
            });
          }
        }
      } else {
        // In a subfolder: show files in this folder and immediate subfolders
        if (relativePath === this.currentFolder) {
          // File is directly in this folder
          result.push(file);
        } else if (relativePath.startsWith(this.currentFolder + '/')) {
          // File is in a subfolder of current folder
          const remainingPath = relativePath.slice(this.currentFolder.length + 1);
          const immediateFolder = remainingPath.split('/')[0];
          if (!seenFolders.has(immediateFolder)) {
            seenFolders.add(immediateFolder);
            result.push({
              isFolder: true,
              folderName: immediateFolder,
              folderPath: this.currentFolder + '/' + immediateFolder,
              fileTime: file.fileTime,
              folder: file.folder
            });
          }
        }
      }
    }

    // Sort: folders first, then by time
    result.sort((a, b) => {
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;
      return Date.parse(b.fileTime) - Date.parse(a.fileTime);
    });

    // Add ".." row at the top if not at root
    if (this.currentFolder) {
      result.unshift({
        isFolder: true,
        isParentDir: true,
        folderName: '..',
        folderPath: this.getParentFolder(),
        fileTime: null
      });
    }

    return result;
  }

  // Navigate to a folder
  navigateToFolder(folderPath: string): void {
    this.currentFolder = folderPath;
    this.selection.clear();
    this.updateDataSourceForCurrentFolder();
    if (this.dataSource.paginator) this.dataSource.paginator.firstPage();
  }

  // Get parent folder path
  private getParentFolder(): string {
    if (!this.currentFolder) return '';
    const parts = this.currentFolder.split('/');
    parts.pop();
    return parts.join('/');
  }

  // Go up one directory
  goUp(): void {
    if (!this.currentFolder) return;
    this.navigateToFolder(this.getParentFolder());
  }

  // Handle row click - navigate if folder, otherwise select
  onItemClick(row: any, event: MouseEvent): void {
    if (row.isFolder) {
      this.navigateToFolder(row.folderPath);
      return;
    }
    this.onRowClick(row, event);
  }

  // Handle double-click - navigate into folder or view file
  onItemDoubleClick(row: any): void {
    if (row.isFolder) {
      this.navigateToFolder(row.folderPath);
    }
  }

  private openDialog(mode: 'copy' | 'move', raw: any): Promise<RelocateRawParameters[]> {
    return new Promise((resolve, reject) => {
      try {
        const ref = this.dialog
          .open<MoveRawDialogComponent, MoveRawDialogData, RelocateRawParameters[]>(MoveRawDialogComponent, {
            minWidth: '500px',
            data: { mode, raws: raw, teamName: this.teamName }
          })
          .afterClosed()
          .subscribe((path) => {
            ref.unsubscribe();
            resolve(path);
          });
      } catch (e) {
        reject(e);
      }
    });
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    public rawService: RawService,
    private uploadService: UploadService,
    public dialog: MatDialog,
    private cacheFileService: CacheFileService,
    private datasetsService: DatasetsService,
    private _settings: SettingsService,
    private cdr: ChangeDetectorRef,
    private widgetService: WidgetService
  ) {}

  ngAfterViewInit(): void {
    if (this.isWidget) {
      this.load(this.teamName);
    } else {
      this._subs.add = this.activatedRoute.params.subscribe((params) => {
        this.load(params.team);
      });
    }

    this._subs.add = this._settings.pollingRate$.subscribe(this.$changePollingRate.bind(this));
  }

  ngOnInit(): void {
    this.$changePollingRate(this._settings.pollingRate);
  }

  @HostListener('dragenter', ['$event'])
  onDragEnter(event: DragEvent) {
    // Only intercept file uploads — let dockview tab drags pass through
    if (!event.dataTransfer?.types?.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;
    this.showDropZone = true;
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent) {
    if (!event.dataTransfer?.types?.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter--;
    if (this.dragCounter === 0) {
      this.showDropZone = false;
    }
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    if (!event.dataTransfer?.types?.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    if (!event.dataTransfer?.types?.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    this.showDropZone = false;
    this.dragCounter = 0;
    if (event.dataTransfer?.files.length) {
      this.handleDroppedFiles(event.dataTransfer.files);
    }
  }

  private async handleDroppedFiles(files: FileList) {
    // Upload directly without dialog - like a file explorer
    const destination = this.currentFolder || '';
    await this.uploadService.uploadFilesDirect(this.teamName, files, destination);
    // Refresh to show uploaded files
    this.isLoading = true;
    this.load(this.teamName);
  }

  // Get the full path of the current folder for uploads
  private getCurrentFolderFullPath(): string {
    // Construct path like /data/{team}/raw or /data/{team}/raw/subfolder
    if (!this.currentFolder) {
      return null; // Will use default raw folder
    }
    // The upload service expects just the subfolder name relative to raw
    return this.currentFolder;
  }

  ngOnDestroy(): void {
    this._subs.dispose();
    if (this._pollingTimer) clearTimeout(this._pollingTimer);
  }

  a = 925;
  b = 5;
  c = 10;
  optimizePageSize() {
    var tableWidth = (<any>this.rawfileTable)._elementRef.nativeElement.clientWidth;
    this._namecat = Math.max(tableWidth - this.a, 0) / this.b + this.c;

    const parent = (<any>document.querySelector('app-raw'));
    if (!parent) return;

    const parentHeight: number = parent.offsetHeight;
    var x = parentHeight;

    this.pageSize = Math.round(0.02 * x) - 4;
    if (this.paginator) {
      this.paginator._changePageSize(this.pageSize);
    }
  }

  applyFilter(filterValue: string) {
    this._filterValue = filterValue.trim().toLowerCase();

    if (this._filterValue) {
      // Enter search mode - search recursively from current folder
      this.isSearchMode = true;
      // Filter rawData to only include files under current folder
      const searchData = this.getFilesUnderCurrentFolder();
      this.dataSource.data = searchData;
      this.dataSource.filter = this._filterValue;
    } else {
      // Exit search mode - show folder view
      this.isSearchMode = false;
      this.dataSource.filter = '';
      this.updateDataSourceForCurrentFolder();
    }

    if (this.dataSource.paginator) this.dataSource.paginator.firstPage();
  }

  // Get all files recursively under the current folder (for search)
  private getFilesUnderCurrentFolder(): any[] {
    if (!this.currentFolder) {
      // At root - return all files
      return this.rawData;
    }

    // Filter to files under current folder
    return this.rawData.filter(file => {
      const folderParts = file.folder.split('/');
      const rawIndex = folderParts.indexOf('raw');
      const relativePath = rawIndex >= 0 ? folderParts.slice(rawIndex + 1).join('/') : '';

      return relativePath === this.currentFolder || relativePath.startsWith(this.currentFolder + '/');
    });
  }

  // Clear search and return to folder view
  clearSearch(): void {
    this._filterValue = '';
    this.isSearchMode = false;
    this.dataSource.filter = '';
    this.updateDataSourceForCurrentFolder();
    if (this.dataSource.paginator) this.dataSource.paginator.firstPage();
  }

  // Create a new folder in the current directory
  async createFolder(): Promise<void> {
    const folderName = await this.dialogs.prompt('Enter folder name:');
    if (!folderName || !folderName.trim()) return;

    const trimmedName = folderName.trim();
    // Construct the full path relative to raw directory
    const newFolderPath = this.currentFolder
      ? `${this.currentFolder}/${trimmedName}`
      : trimmedName;

    try {
      await this.rawService.createFolder(this.teamName, newFolderPath);
      // Refresh to show the new folder
      this.load(this.teamName);
    } catch (error) {
      console.error('Failed to create folder:', error);
      this.dialogs.alert('Failed to create folder. Please try again.');
    }
  }

  load(teamName: string) {
    this.teamName = teamName;
    const sub = this.rawService.get(teamName).subscribe((response: any[]) => {
      sub.unsubscribe();
      this.isLoading = false;
      this.lastUpdated = new Date();
      this.$loadNewRawData(response);
    });
  }

  public async refreshCacheData(): Promise<void> {
    this.isPolling = true;
    const rawData = await this.rawService.getAsync(this.teamName);
    const cacheData = Object.fromEntries(
      rawData.map(({ fullPath, hasCacheError, hasCacheFile, cacheFileScreen }) => [fullPath, { hasCacheError, hasCacheFile, cacheFileScreen }])
    );

    for (const rds of this.rawData) {
      if (rds.fullPath in cacheData) Object.assign(rds, cacheData[rds.fullPath]);
    }

    this.lastUpdated = new Date();
    this.isPolling = false;
  }

  applyTableSettings() {
    setTimeout(() => {
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;

      // Custom sort to keep ".." always at top
      this.dataSource.sortData = (data: any[], sort: MatSort) => {
        if (!sort.active || sort.direction === '') {
          return data;
        }

        return data.sort((a, b) => {
          // ".." always comes first
          if (a.isParentDir) return -1;
          if (b.isParentDir) return 1;

          const isAsc = sort.direction === 'asc';
          const valueA = a[sort.active];
          const valueB = b[sort.active];

          // Handle null/undefined
          if (valueA == null && valueB == null) return 0;
          if (valueA == null) return isAsc ? -1 : 1;
          if (valueB == null) return isAsc ? 1 : -1;

          // Compare
          let comparison = 0;
          if (typeof valueA === 'string' && typeof valueB === 'string') {
            comparison = valueA.localeCompare(valueB);
          } else {
            comparison = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
          }

          return isAsc ? comparison : -comparison;
        });
      };

      this.optimizePageSize();
    }, 0);
  }

  onRowClick(row: any, event: MouseEvent) {
    if (event.shiftKey && this.lastClickedRow) {
      // Shift-click: select range between last clicked and current
      event.preventDefault();
      this.selectRange(this.lastClickedRow, row);
    } else {
      // Normal click: toggle single row
      this.selection.toggle(row);
    }
    this.lastClickedRow = row;
    this.cdr.detectChanges();
  }

  private selectRange(fromRow: any, toRow: any) {
    const data = this.dataSource.filteredData;
    const fromIndex = data.findIndex((r: any) => r.fullPath === fromRow.fullPath);
    const toIndex = data.findIndex((r: any) => r.fullPath === toRow.fullPath);

    if (fromIndex === -1 || toIndex === -1) return;

    const startIndex = Math.min(fromIndex, toIndex);
    const endIndex = Math.max(fromIndex, toIndex);

    for (let i = startIndex; i <= endIndex; i++) {
      if (!this.selection.isSelected(data[i])) {
        this.selection.select(data[i]);
      }
    }
  }

  toggleSelection(row: any) {
    this.selection.toggle(row);
  }

  closeCacheView() {
    this.view = 'TABLE';
  }

  refresh() {
    this.isLoading = true;
    this.load(this.teamName);
  }

  unselectAll() {
    this.dataSource.filteredData.forEach((row: any) => {
      if (this.selection.selected.find((x) => x.fullPath == row.fullPath)) this.toggleSelection(row);
    });
  }

  selectAll() {
    this.dataSource.filteredData.forEach((row: any) => {
      if (!this.selection.selected.find((x) => x.fullPath == row.fullPath)) this.toggleSelection(row);
    });
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filteredData.length;
    return numSelected === numRows;
  }

  async openFileUpload() {
    // Create a hidden file input and trigger it
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = async () => {
      if (input.files && input.files.length > 0) {
        // Use current folder as destination (simplified workflow)
        const destination = this.getCurrentFolderFullPath();
        const results = await this.uploadService.promptForUploadWithFiles(
          this.teamName,
          null,
          input.files,
          destination
        );
        if (results && results.reload) {
          this.isLoading = true;
          this.load(this.teamName);
        }
      }
    };
    input.click();
  }

  async onViewCacheFile($ev: PointerEvent, row) {
    $ev.stopPropagation();

    if (this.widgetService.grid) {
      this.widgetService.grid.addTerminalWidget(
        `screen -T xterm-256color -d -r ${row.cacheFileScreen}`,
        `Caching: ${row.fileName}`
      );
    }
  }

  async onViewCacheLog($ev: PointerEvent, row) {
    $ev.stopPropagation();
    this.view = 'CACHE';
    if (this.screenInfo != null) this.terminal.disconnect();
    this.screenInfo = null;
    const path = `${row.folder}/.adapter/create_cachefile_${row.fileName.split('.')[0]}.${row.fileName.split('.')[1]}.txt`;
    this.logInfo = { path };
  }

  isCacheable(row): boolean {
    const fileName = row.fileName?.toLowerCase() || '';
    return fileName.endsWith('.csv') || fileName.endsWith('.csv.gz') || fileName.endsWith('.parquet');
  }

  private static CACHE_CONFIRM_STORAGE_KEY = 'lit.skipCacheConfirmDialog';

  async onCacheFile($ev: PointerEvent, row) {
    $ev.stopPropagation();

    // Skip dialog if user previously checked "Don't show again"
    if (ConfirmDialogComponent.shouldSkip(RawComponent.CACHE_CONFIRM_STORAGE_KEY)) {
      this.cacheFileService.createCacheFileAsync(this.teamName, row.fullPath);
      setTimeout(() => this.load(this.teamName), 100);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Create Cache File',
        message: `Caching converts "${row.fileName}" to a format optimized for fast random access and viewing.\n\nThis is useful for large CSV or Parquet files you plan to explore frequently. The process runs in the background and may take a few minutes for large files.\n\nProceed with caching?`,
        confirmText: 'Cache',
        cancelText: 'Cancel',
        storageKey: RawComponent.CACHE_CONFIRM_STORAGE_KEY
      }
    });

    const result = await lastValueFrom(dialogRef.afterClosed());
    if (result) {
      this.cacheFileService.createCacheFileAsync(this.teamName, row.fullPath);
      setTimeout(() => this.load(this.teamName), 100);
    }
  }

  $createCacheClick(items = []) {
    if (items.length == 0) {
      items = this.selection.selected;
    }
    for (const raw of items) {
      if (!raw) continue;

      if (raw.cacheFileScreen) {
        console.warn(`Already caching ${raw.fullPath}.`);
        continue;
      }

      this.cacheFileService.createCacheFileAsync(this.teamName, raw.fullPath);
    }

    setTimeout(() => {
      this.load(this.teamName);
    }, 100);
  }

  $relocateClick(mode: 'move' | 'copy') {
    this.isLoading = true;
    this.openDialog(mode, this.selection.selected).then((response) => {
      if (!response) {
        this.isLoading = false;
        return;
      }

      const sub = this.rawService.relocate(this.teamName, mode, response).subscribe((response) => {
        sub.unsubscribe();
        this.$loadNewRawData(response);
      });
    });
  }

  async $deleteClick() {
    if (!await this.dialogs.confirm('Are you sure you want to delete these raw data files?')) return;

    this.isLoading = true;
    const sub = this.rawService
      .delete(
        this.teamName,
        this.selection.selected.map(({ host: hostName, fullPath: path }) => ({ hostName, path }))
      )
      .subscribe((response) => {
        sub.unsubscribe();
        this.$loadNewRawData(response);
      });
  }

  $viewClick($event, row) {
    $event.stopPropagation();
    if (!row.hasCacheFile && row.fullPath.includes('.csv')) {
      this.dialogs.alert('Please create a cache file before viewing this asset.');
      return;
    }

    if (this.isWidget) {
      let fileType = null;
      if (row.fullPath.endsWith('.csv') || row.fullPath.endsWith('.csv.gz')) {
        fileType = 'csv';
      } else {
        fileType = row.fullPath.split('/').pop().split('.')[1];
      }

      this.onViewData.emit({ path: row.fullPath, fileType });
    } else {
      this.router.navigate([this.teamName, 'data', 'data-viewer', encodeURI(row.fullPath.replace(/\//g, '\\'))]);
    }
  }

  public $changePollingRate(newRate: number): void {
    if (this._pollingTimer) clearInterval(this._pollingTimer);
    this._pollingTimer = setInterval(this.refreshCacheData.bind(this), newRate) as unknown as number;
  }

  // Inline dataset chip methods
  getAvailableDatasets(row: any, filter: string): string[] {
    const currentDatasets = row.datasets || [];
    let available = this.allDatasets.filter(ds => !currentDatasets.includes(ds));

    if (filter && filter.trim()) {
      const lowerFilter = filter.toLowerCase();
      available = available.filter(ds => ds.toLowerCase().includes(lowerFilter));
    }

    return available;
  }

  datasetExists(name: string): boolean {
    if (!name || !name.trim()) return true;
    return this.allDatasets.some(ds => ds.toLowerCase() === name.toLowerCase());
  }

  isDatasetOperationLoading(row: any): boolean {
    return this.datasetOperationLoading.get(row.fullPath) || false;
  }

  async onAddToDataset(row: any, datasetName: string, menuTrigger?: MatMenuTrigger) {
    if (!datasetName || !datasetName.trim()) return;

    const trimmedName = datasetName.trim();

    // Close the menu immediately to show user we're processing
    if (menuTrigger) {
      menuTrigger.closeMenu();
    }

    // Set loading state for this row
    this.datasetOperationLoading.set(row.fullPath, true);

    try {
      // Create dataset if it doesn't exist
      if (!this.datasetExists(trimmedName)) {
        await this.datasetsService.add(this.teamName, trimmedName);
        this.allDatasets.push(trimmedName);
      }

      // Add file to dataset
      await this.datasetsService.update(this.teamName, trimmedName, row.fullPath, true);

      // Update local state
      if (!row.datasets) {
        row.datasets = [];
      }
      if (!row.datasets.includes(trimmedName)) {
        row.datasets.push(trimmedName);
      }
    } finally {
      this.datasetOperationLoading.set(row.fullPath, false);
    }
  }

  async onRemoveFromDataset(row: any, datasetName: string) {
    // Optimistic UI: hide chip immediately, show spinner
    const originalDatasets = [...(row.datasets || [])];
    row.datasets = row.datasets.filter((ds: string) => ds !== datasetName);
    this.datasetOperationLoading.set(row.fullPath, true);

    try {
      await this.datasetsService.update(this.teamName, datasetName, row.fullPath, false);
    } catch (error) {
      // Restore chip on error
      row.datasets = originalDatasets;
      throw error;
    } finally {
      this.datasetOperationLoading.set(row.fullPath, false);
    }
  }

  // Called when the dataset menu opens - trigger click to open autocomplete after menu animation completes
  onDatasetMenuOpened() {
    const menuPanel = document.querySelector('.mat-mdc-menu-panel');
    if (!menuPanel) return;

    const overlayPane = menuPanel.closest('.cdk-overlay-pane');
    if (!overlayPane) return;

    const onAnimationEnd = (e: AnimationEvent) => {
      if (e.animationName === '_mat-menu-enter') {
        overlayPane.removeEventListener('animationend', onAnimationEnd as EventListener);
        const input = document.querySelector('.dataset-menu-content input') as HTMLInputElement;
        if (input) {
          input.click();
        }
      }
    };

    overlayPane.addEventListener('animationend', onAnimationEnd as EventListener);
  }
}
